import getLanguage from 'locales/getLanguage'
import clsx from 'clsx'
import { buttonDataId } from 'consts'
import { ButtonClose } from 'components/assets'
import { AvailableCampaigns } from 'entities/Config'
import { t } from '@lingui/macro'
import {
  getConsentMessageClosed,
  getConsultId,
  setConsentMessageClosed,
} from 'utils/sessionStorage'
import { getGlobalConfig } from 'utils/getTenantSpecificValues'
import type { GlobalConfig, ThemeConfig } from 'entities/Config'
import styles from 'components/atoms/ChatButton/index.module.css'

const labelsByLanguage = {
  de: 'Open Pharmacy Assistant', // TODO Update translation once we get it
  en: 'Open Pharmacy Assistant',
  it: 'Open Pharmacy Assistant',
  fr: 'Open Pharmacy Assistant',
  nl: 'Open Pharmacy Assistant',
  default: 'Open Pharmacy Assistant',
} as any

const {
  chatButton,
  chatButtonBackside,
  chatButtonFrontside,
  button: buttonStyle,
  buttonLabel,
  chatButtonBranded,
  campaignActive,
} = styles

const generateWelcomeMessage = ({
  theme,
  isConsentGiven,
  globalConfig,
}: {
  theme: ThemeConfig
  isConsentGiven: boolean
  globalConfig?: GlobalConfig
}): string | undefined => {
  let welcomeMessage = theme?.welcomeMessage
  const consentMessageClosed = getConsentMessageClosed()

  if (!isConsentGiven && consentMessageClosed) {
    return undefined
  }

  if (!isConsentGiven && globalConfig) {
    const isCampaign = theme.id !== AvailableCampaigns.default
    const campaignMessage =
      theme?.consentMessage ??
      globalConfig?.checkConsentCampaignText?.replace(/\$name/g, theme.name)

    const fallbackMessage = isCampaign
      ? campaignMessage
      : globalConfig?.checkConsentDefaultText

    welcomeMessage = theme?.consentMessage ?? fallbackMessage
  }

  return welcomeMessage
}

const createSvgElement = (elementName: string) =>
  document.createElementNS('http://www.w3.org/2000/svg', elementName)

const generateChatButtonIcon = (theme?: ThemeConfig) => {
  const imageContainer = document.createElement('div')
  imageContainer.className = clsx(chatButton, chatButtonFrontside)

  const icon = createSvgElement('svg')
  icon.setAttribute('width', '60')
  icon.setAttribute('height', '60')
  icon.setAttribute('fill', 'none')

  if (theme && theme.id !== AvailableCampaigns.default && theme?.buttonLogo) {
    const border = 3
    const avatar = createSvgElement('image')
    const defs = createSvgElement('defs')

    const outerCircle = createSvgElement('circle')
    outerCircle.setAttribute('cx', '30')
    outerCircle.setAttribute('cy', '30')
    outerCircle.setAttribute('r', `30`)
    outerCircle.setAttribute('fill', `var(--primary)`)

    const innerCircle = createSvgElement('circle')
    innerCircle.setAttribute('cx', '30')
    innerCircle.setAttribute('cy', '30')
    innerCircle.setAttribute('r', `27`)
    innerCircle.setAttribute('fill', `#fff`)

    icon.appendChild(outerCircle)
    icon.appendChild(innerCircle)

    const clipPath = createSvgElement('clipPath')
    clipPath.setAttribute('id', 'avatar-clip')

    const clipCircle = createSvgElement('circle')
    clipCircle.setAttribute('cx', '30')
    clipCircle.setAttribute('cy', '30')
    clipCircle.setAttribute('r', `${30 - border}`)

    clipPath.appendChild(clipCircle)
    defs.appendChild(clipPath)
    icon.appendChild(defs)

    avatar.setAttribute('href', theme.buttonLogo)
    avatar.setAttribute('width', '60')
    avatar.setAttribute('height', '60')
    avatar.setAttribute('style', 'clip-path: url(#avatar-clip);')
    icon.appendChild(avatar)
  } else {
    const speechBubbles = createSvgElement('path')
    speechBubbles.setAttribute(
      'd',
      'M37.231 32.966a.942.942 0 1 1-1.885 0 .942.942 0 0 1 1.885 0Zm.252-11.835c-1.017-.277-2.218-.111-3.57.494a.53.53 0 1 0 .433.967c1.123-.503 2.085-.65 2.859-.44.616.17 1.096.59 1.317 1.158a1.882 1.882 0 0 1-.183 1.7c-.226.356-.508.677-.807 1.018-.342.388-.694.79-.978 1.267a5.687 5.687 0 0 0-.803 2.905.53.53 0 0 0 1.059 0c0-.828.226-1.646.653-2.363.235-.394.54-.742.864-1.11.313-.356.636-.724.907-1.152a2.929 2.929 0 0 0 .274-2.648c-.343-.883-1.08-1.538-2.025-1.796ZM22.363 42.62c-4.484-2.09-5.282-5.87-5.42-6.978a.528.528 0 1 0-1.05.13c.154 1.245 1.045 5.487 6.023 7.808a.528.528 0 0 0 .703-.256.53.53 0 0 0-.256-.704ZM60 30c0 16.569-13.431 30-30 30C13.431 60 0 46.569 0 30 0 13.431 13.431 0 30 0c16.569 0 30 13.431 30 30ZM21.994 17.77a.53.53 0 1 0 1.056.074l.192-2.765a.53.53 0 1 0-1.056-.074l-.192 2.765ZM16.8 16.258l2.313 3.053a.53.53 0 1 0 .844-.64l-2.313-3.052a.53.53 0 0 0-.844.64Zm-1.836 5.321 2.66.776a.529.529 0 1 0 .296-1.016l-2.66-.776a.53.53 0 0 0-.296 1.016ZM33.072 40.13c-4.897-.974-8.983-4.634-10.374-9.575-.629-2.234-.62-4.53-.102-6.69-2.56.204-5.051 1.276-6.944 3.2a10.954 10.954 0 0 0-3.135 8.542l-.003-.002s.195 7.381 7.985 11.013c3.46 1.613 6.838 1.758 9.09 1.587.637-.048.81-.913.231-1.184a10.071 10.071 0 0 1-2.276-1.468c-.209-.178-.139-.512.115-.616a10.91 10.91 0 0 0 4.089-2.926 11.03 11.03 0 0 0 1.324-1.88ZM49.3 28.986c-.386-9.703-7.993-13.126-7.993-13.126v.004a12.377 12.377 0 0 0-10.27-.46c-5.812 2.327-9.016 8.837-7.32 14.864 1.934 6.868 9.18 10.658 15.874 8.431a12.326 12.326 0 0 0 4.833-2.984c.22-.22.597-.147.692.148.356 1.11.502 2.136.537 3.013.03.72 1.007.918 1.334.276 1.161-2.273 2.484-5.855 2.313-10.166Z'
    )
    speechBubbles.setAttribute('fill', '#ED0334')

    const iconBackground = createSvgElement('circle')
    iconBackground.setAttribute('cx', '30')
    iconBackground.setAttribute('cy', '30')
    iconBackground.setAttribute('r', `30`)
    iconBackground.setAttribute('fill', `#FCF7F7`)

    icon.appendChild(iconBackground)
    icon.appendChild(speechBubbles)
  }

  imageContainer.appendChild(icon)
  return imageContainer
}

interface CreateOpenButtonOptions {
  withCounter?: boolean
  isConsentGiven: boolean
}

const createOpenButton = (
  theme: ThemeConfig,
  { withCounter, isConsentGiven }: CreateOpenButtonOptions
) => {
  const consultId = getConsultId(theme.id)
  const lang = getLanguage()
  const globalConfig = getGlobalConfig()!
  const chatLabel = labelsByLanguage[lang]
    ? labelsByLanguage[lang]
    : labelsByLanguage.default

  const button = document.createElement('button')

  button.id = buttonDataId
  button.type = 'button'
  button.dataset.testid = 'modal-button'

  button.className = clsx(buttonStyle)
  button.ariaLabel = t`Open Chatbot`

  if (withCounter) {
    button.classList.add(styles.withCounter)
  }

  const label = document.createElement('span')
  label.className = buttonLabel
  label.textContent = chatLabel

  const imageContainer = generateChatButtonIcon(theme)

  const closeImage = document.createElement('img')
  closeImage.className = clsx(chatButton, chatButtonBackside)
  closeImage.alt = ''
  closeImage.src = ButtonClose

  const wiggleContainer = document.createElement('div')
  wiggleContainer.className = styles.wiggleContainer
  wiggleContainer.appendChild(imageContainer)
  wiggleContainer.appendChild(closeImage)

  if (theme.id !== AvailableCampaigns.default) {
    wiggleContainer.classList.add(campaignActive)
  }

  const counter = document.createElement('div')
  counter.className = styles.counter
  counter.innerText = '1'
  button.appendChild(counter)

  button.appendChild(label)
  button.appendChild(wiggleContainer)

  const welcomeMessage = generateWelcomeMessage({
    theme,
    isConsentGiven,
    globalConfig,
  })

  if (!consultId && welcomeMessage) {
    button.className = `${button.className} ${chatButtonBranded}`
    const welcomeMessageContainer = document.createElement('div')
    welcomeMessageContainer.className = styles.welcomeMessageContainer
    welcomeMessageContainer.ariaLabel = t`Open Chatbot`

    const hideWelcomeMessage = (e: MouseEvent) => {
      e.stopPropagation()
      welcomeMessageContainer.classList.add(styles.hideWelcomeMessage)

      if (globalConfig.checkConsentEnabled && !isConsentGiven) {
        setConsentMessageClosed()
      }
    }

    const welcomeMessageElement = document.createElement('div')
    welcomeMessageElement.dataset.testid = 'chat-consult-welcome-message'
    welcomeMessageElement.innerHTML = `<p>${welcomeMessage}</p>`
    welcomeMessageElement.className = clsx(styles.welcomeMessage)

    const closeButton = document.createElement('button')
    closeButton.dataset.testid = 'chat-consult-close-button'
    closeButton.className = styles.welcomeMessageCloseButton
    closeButton.type = 'button'
    closeButton.addEventListener('click', hideWelcomeMessage)
    closeButton.addEventListener('mouseenter', () => {
      button.classList.add(styles.blockWiggle)
    })
    closeButton.ariaLabel = t`Close the chatbot information window`

    closeButton.addEventListener('mouseleave', () => {
      button.classList.remove(styles.blockWiggle)
    })

    welcomeMessageContainer.appendChild(welcomeMessageElement)
    welcomeMessageContainer.appendChild(closeButton)

    button.appendChild(welcomeMessageContainer)
  }

  button.classList.add(styles.initialWiggle)

  setTimeout(() => {
    button.classList.remove(styles.initialWiggle)
  }, 1200)

  return button
}

export default createOpenButton
